import { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from 'core/store/authStore';
import { listUsers, updateUser } from 'features/admin/services/adminApi';
import type { AdminUser, AppRole } from 'features/admin/services/adminApi';
import { ROUTES } from 'core/router/routes';
import 'pages/home/components/homepage/homepage.css';

const ROLE_LABELS: Record<string, { label: string; color: string }> = {
  customer: { label: 'Customer', color: 'bg-blue-50 text-blue-600' },
  provider: { label: 'Provider', color: 'bg-violet-50 text-violet-600' },
};

const APP_ROLES: AppRole[] = ['customer', 'provider'];

export default function AdminUsers() {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const rootRef = useRef<HTMLDivElement>(null);

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Role change modal state
  const [promoting, setPromoting] = useState<AdminUser | null>(null);
  const [newRole, setNewRole] = useState<AppRole>('provider');
  const [promoteLoading, setPromoteLoading] = useState(false);
  const [promoteError, setPromoteError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const [search, setSearch] = useState('');

  // Intersection observer animation
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.target.classList.toggle('in-view', e.isIntersecting)),
      { threshold: 0.1 }
    );
    root.querySelectorAll('.reveal').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [users]);

  const fetchUsers = async (p: number) => {
    setLoading(true);
    setError(null);
    try {
      const data = await listUsers(p);
      setUsers(data.users);
      setTotal(data.total);
      setTotalPages(data.totalPages);
      setPage(p);
    } catch {
      setError('Failed to load users.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(1); }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const openPromote = (u: AdminUser) => {
    setPromoting(u);
    setNewRole(u.appRole as AppRole);
    setPromoteError(null);
    setSuccessMsg(null);
  };

  const handleConfirmRoleChange = async () => {
    if (!promoting) return;
    setPromoteLoading(true);
    setPromoteError(null);
    try {
      const updated = await updateUser(promoting.id, { appRole: newRole });
      setUsers((prev) => prev.map((u) => (u.id === updated.id ? { ...u, appRole: updated.appRole } : u)));
      const wasPromoted = newRole === 'provider' && promoting.appRole !== 'provider';
      setSuccessMsg(
        wasPromoted
          ? `${promoting.firstName} has been promoted to Provider. They'll complete onboarding on next login.`
          : `${promoting.firstName}'s role has been updated to ${ROLE_LABELS[newRole]?.label ?? newRole}.`
      );
      setPromoting(null);
    } catch (err: any) {
      setPromoteError(err?.response?.data?.message ?? 'Failed to update role.');
    } finally {
      setPromoteLoading(false);
    }
  };

  const displayedUsers = search.trim()
    ? users.filter(
        (u) =>
          `${u.firstName} ${u.lastName ?? ''} ${u.email}`.toLowerCase().includes(search.toLowerCase())
      )
    : users;

  const firstName = user?.displayName?.split(' ')[0] ?? 'Admin';

  return (
    <div ref={rootRef} className="min-h-screen bg-background-light font-display">
      {/* ── Header ── */}
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-slate-100 px-6 py-3">
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="bg-primary p-1.5 rounded-xl shadow-lg shadow-primary/25">
              <span className="material-symbols-outlined text-white text-[22px]" style={{ fontVariationSettings: '"FILL" 1' }}>
                directions_run
              </span>
            </div>
            <span className="text-[1.2rem] font-black tracking-tighter">Errandi</span>
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline-flex items-center gap-1.5 text-xs font-black uppercase tracking-widest text-slate-400 bg-slate-50 px-3 py-1.5 rounded-full border border-slate-100">
              <span className="material-symbols-outlined text-[14px]">shield</span>Admin
            </span>
            <Link to={ROUTES.ADMIN.VEHICLES} className="text-xs font-bold text-primary">Vehicles</Link>
            <Link to={ROUTES.ADMIN.DRIVERS} className="text-xs font-bold text-primary">Drivers</Link>
            <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-white text-xs font-black">
              {firstName[0]}
            </div>
            <button onClick={handleLogout} className="text-xs font-black text-slate-400 hover:text-primary transition-colors">
              Log out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        {/* ── Page title ── */}
        <div className="reveal opacity-0 translate-y-4 transition-all duration-500 mb-8">
          <p className="text-xs font-black uppercase tracking-widest text-primary mb-1">Admin</p>
          <h1 className="text-3xl font-black tracking-tight text-slate-900">User Management</h1>
          <p className="text-slate-500 text-sm mt-1">{total} registered users</p>
        </div>

        {/* ── Success banner ── */}
        {successMsg && (
          <div className="reveal opacity-0 translate-y-4 transition-all duration-500 mb-6 flex items-start gap-3 bg-green-50 border border-green-200 rounded-2xl px-5 py-4">
            <span className="material-symbols-outlined text-green-600 text-[20px] mt-0.5" style={{ fontVariationSettings: '"FILL" 1' }}>check_circle</span>
            <div>
              <p className="text-sm font-bold text-green-800">{successMsg}</p>
              {successMsg.includes('Fleet Owner') && (
                <p className="text-xs text-green-600 mt-1">Their refresh tokens have been revoked — the new role takes effect on their next login.</p>
              )}
            </div>
            <button onClick={() => setSuccessMsg(null)} className="ml-auto text-green-400 hover:text-green-600">
              <span className="material-symbols-outlined text-[18px]">close</span>
            </button>
          </div>
        )}

        {/* ── Search ── */}
        <div className="reveal opacity-0 translate-y-4 transition-all duration-500 mb-5">
          <div className="relative max-w-sm">
            <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-300 text-[18px]">search</span>
            <input
              type="text"
              placeholder="Search by name or email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 text-sm font-medium border-2 border-slate-100 focus:border-primary/50 focus:outline-none rounded-2xl bg-white text-slate-800 placeholder:text-slate-300 transition-colors"
            />
          </div>
        </div>

        {/* ── Table ── */}
        {loading ? (
          <div className="flex justify-center py-20">
            <span className="material-symbols-outlined text-primary text-4xl animate-spin">progress_activity</span>
          </div>
        ) : error ? (
          <div className="text-center py-20 text-red-500 font-medium">{error}</div>
        ) : (
          <div className="reveal opacity-0 translate-y-4 transition-all duration-500 bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="text-left px-6 py-4 text-xs font-black uppercase tracking-widest text-slate-400">User</th>
                  <th className="text-left px-6 py-4 text-xs font-black uppercase tracking-widest text-slate-400">Role</th>
                  <th className="text-left px-6 py-4 text-xs font-black uppercase tracking-widest text-slate-400 hidden md:table-cell">Status</th>
                  <th className="text-left px-6 py-4 text-xs font-black uppercase tracking-widest text-slate-400 hidden lg:table-cell">Joined</th>
                  <th className="px-6 py-4" />
                </tr>
              </thead>
              <tbody>
                {displayedUsers.length === 0 ? (
                  <tr><td colSpan={5} className="text-center py-16 text-slate-400 font-medium">No users found</td></tr>
                ) : displayedUsers.map((u, i) => {
                  const roleInfo = ROLE_LABELS[u.appRole] ?? { label: u.appRole, color: 'bg-slate-100 text-slate-600' };
                  return (
                    <tr key={u.id} className={`border-b border-slate-50 hover:bg-slate-50/60 transition-colors ${i % 2 === 0 ? '' : ''}`}>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary text-xs font-black shrink-0">
                            {u.firstName[0]}{u.lastName?.[0] ?? ''}
                          </div>
                          <div>
                            <p className="font-bold text-slate-800">{u.firstName} {u.lastName ?? ''}</p>
                            <p className="text-xs text-slate-400">{u.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-black ${roleInfo.color}`}>
                          {roleInfo.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 hidden md:table-cell">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-black ${u.isActive ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-500'}`}>
                          {u.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 hidden lg:table-cell text-xs text-slate-400 font-medium">
                        {new Date(u.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </td>
                      <td className="px-6 py-4 text-right space-x-2">
                        {u.appRole === 'provider' && (
                          <Link
                            to={ROUTES.ADMIN.USER_DETAIL(u.id)}
                            className="text-xs font-black text-slate-600 hover:bg-slate-100 px-3 py-1.5 rounded-xl transition-colors inline-block"
                          >
                            Licence / KYC
                          </Link>
                        )}
                        <button
                          onClick={() => openPromote(u)}
                          className="text-xs font-black text-primary hover:bg-primary/10 px-3 py-1.5 rounded-xl transition-colors"
                        >
                          Change role
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100">
                <p className="text-xs font-medium text-slate-400">Page {page} of {totalPages}</p>
                <div className="flex gap-2">
                  <button
                    disabled={page <= 1}
                    onClick={() => fetchUsers(page - 1)}
                    className="px-3 py-1.5 text-xs font-black rounded-xl border border-slate-200 hover:border-primary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    Previous
                  </button>
                  <button
                    disabled={page >= totalPages}
                    onClick={() => fetchUsers(page + 1)}
                    className="px-3 py-1.5 text-xs font-black rounded-xl border border-slate-200 hover:border-primary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {/* ── Role change modal ── */}
      {promoting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-8">
            <div className="flex items-start justify-between mb-6">
              <div>
                <h2 className="text-xl font-black text-slate-900">Change role</h2>
                <p className="text-sm text-slate-500 mt-1">
                  {promoting.firstName} {promoting.lastName ?? ''} &middot; {promoting.email}
                </p>
              </div>
              <button onClick={() => setPromoting(null)} className="text-slate-300 hover:text-slate-600 transition-colors mt-1">
                <span className="material-symbols-outlined text-[20px]">close</span>
              </button>
            </div>

            <div className="space-y-3 mb-6">
              {APP_ROLES.map((role) => {
                const info = ROLE_LABELS[role];
                const isProviderPromotion = role === 'provider' && promoting.appRole !== 'provider';
                return (
                  <label key={role} className={`flex items-center gap-4 p-4 rounded-2xl border-2 cursor-pointer transition-all ${newRole === role ? 'border-primary bg-primary/5' : 'border-slate-100 hover:border-slate-200'}`}>
                    <input
                      type="radio"
                      name="role"
                      value={role}
                      checked={newRole === role}
                      onChange={() => setNewRole(role)}
                      className="sr-only"
                    />
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${newRole === role ? 'border-primary' : 'border-slate-300'}`}>
                      {newRole === role && <div className="w-2 h-2 rounded-full bg-primary" />}
                    </div>
                    <div className="flex-1">
                      <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-black ${info.color}`}>{info.label}</span>
                      {isProviderPromotion && (
                        <p className="text-xs text-slate-400 mt-1">User will complete provider onboarding on next login. Their current session will be invalidated.</p>
                      )}
                    </div>
                  </label>
                );
              })}
            </div>

            {promoteError && (
              <p className="text-sm text-red-500 font-medium mb-4">{promoteError}</p>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => setPromoting(null)}
                className="flex-1 py-3 rounded-2xl border-2 border-slate-200 text-sm font-black text-slate-600 hover:border-slate-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmRoleChange}
                disabled={promoteLoading || newRole === promoting.appRole}
                className="flex-1 py-3 rounded-2xl bg-primary text-white text-sm font-black hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-lg shadow-primary/25"
              >
                {promoteLoading ? 'Saving…' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
